import React, { useState, useRef, useEffect } from 'react';
import toast from 'react-hot-toast';
import printJS from 'print-js';
import ApiClient from '../../utils/ApiClient';
import { PrinterIcon, Cog6ToothIcon, TagIcon } from '@heroicons/react/24/outline';

// Task 57 (owner, verbatim): "the paper type will always be 4 x 6 -- please auto select that as
// the default." Was PackageLabel_Letter_6.
export const DEFAULT_LABEL_PAGE_TYPE = 'PackageLabel_Thermal';

// v0 getLabels PageTypes that make sense for our small-parcel flow -- Thermal 4x6 (the default)
// first, the sheet-fed formats as fallbacks for stations without a thermal printer.
export const PAGE_TYPES = [
    { value: 'PackageLabel_Thermal', label: 'Thermal 4×6 (default)' },
    { value: 'PackageLabel_Letter_6', label: 'Letter — 6 per page' },
    { value: 'PackageLabel_Letter_2', label: 'Letter — 2 per page' },
    { value: 'PackageLabel_Plain_Paper', label: 'Plain paper — 1 per page' },
];

// Task 48 originated this (ConfirmedShipments.js): streams the label PDF through our backend
// (which proxies Amazon's presigned zip/pdf URL and unwraps single-PDF zips). Task 49 pulled the
// fetch/blob/error dance out to this shared helper so every "Print labels" entry point (Confirmed
// Shipments page, Shipment Runs table, Shipment Planner loaded-run banner) does the exact same
// thing instead of re-deriving it.
//
// Task 52 (owner, verbatim): "we need to select the button and the printer automatically prints,
// no downloading, opening a pdf, and then printing." -- printShipmentLabels() sends the PDF
// straight to the browser print command instead of opening a tab. On a warehouse station running
// Chrome with --kiosk-printing this prints silently to the default printer with zero clicks;
// everywhere else it still opens the native print dialog, just with the PDF already loaded (one
// click instead of open-tab-then-Ctrl+P). openShipmentLabelsPdf() keeps the old open-in-a-new-tab
// behavior as a small secondary affordance for saving/reprinting elsewhere -- both share
// fetchLabelBlob()/describeBlobError() so the two paths can't drift.
//
// Task 54 (owner, live bug): Task 52's original iframe.contentWindow.print() left the print
// preview blank / printed nothing -- Chrome's known failure mode when print() fires before the
// embedded PDF viewer has finished rendering or taken focus. Swapped to the print-js library
// (MIT, ~5KB), which does the load/focus/print-wait dance reliably cross-browser. It still prints
// against a hidden iframe under the hood, so the offscreen-iframe kiosk-printing behavior is
// unchanged -- only the "wait for the PDF to actually be ready" part is different.
//
// Task 56 (owner, live bug): after the Task 54 deploy, Print threw the error toast ("could not
// open pdf") in production. Root cause, confirmed in node_modules/print-js/dist/print.js
// (src/js/pdf.js): print-js's non-base64 pdf path re-fetches `printable` itself via a second XHR
// (`req.open('GET', params.printable, true)`) and treats any status outside [200, 201] as failure
// -- and XHR against a blob: URL reports inconsistent/zero status in some browsers/timings, so a
// perfectly good blob URL could still fire onError. Passing the bytes directly as base64
// (`{base64: true}`) skips that second fetch -- and the blob-URL-revocation race that came with it
// -- entirely (see printPdfBlob below). Separately, print-js's own Firefox detection
// (`typeof InstallTrigger !== 'undefined'`, browser.js) is stale -- InstallTrigger was removed
// from modern Firefox -- so its Firefox-specific iframe styling never kicks in there, and Firefox
// is a known no-go for driving PDF printing through print-js's iframe. We now detect Firefox via
// user-agent up front and skip straight to the open-in-tab fallback instead of letting it fail
// first.
//
// Task 57 (owner, verbatim): "sometimes there are multiple labels/boxes per shipment - instead of
// clicking each button, can we have a print all labels button that automatically opens the print
// dialogue box for each box that is part of that shipment." The backend already sends every
// cached box id to Amazon's getLabels as PackageLabelsToPrint, so Amazon returns ONE PDF with
// every box's label -- "print all" is just fetching that one PDF and printing it, same as today,
// just always defaulting to the full box count instead of a picker. Owner clarification: "print
// all" also needs to span every SHIPMENT in a workflow (an ASIN can get 5 shipments), which the
// new backend GET .../labels/{planId}/all endpoint handles server-side (fetch + PDFBox-merge per
// shipment, skipping any that aren't ready yet and reporting them via the X-Labels-Skipped
// header). Owner addition #2: a second "Print item labels (DYMO)" action for the per-UNIT FNSKU
// labels (2024-03-20 createMarketplaceItemLabels), sized for a DYMO LabelWriter 550, with the
// same plan-level "all shipments" merge + per-shipment reprint pattern.
async function fetchLabelBlob(planId, shipmentId, { pageType = DEFAULT_LABEL_PAGE_TYPE, count } = {}) {
    const params = { pageType };
    const n = parseInt(count, 10);
    if (!isNaN(n) && n > 0) params.count = n;
    const res = await ApiClient.get(
        `/api/shipment-planner/labels/${planId}/${shipmentId}`,
        { params, responseType: 'blob', timeout: 90000 }
    );
    const contentType = res.headers['content-type'] || 'application/pdf';
    return new Blob([res.data], { type: contentType });
}

// Streams a merged PDF (box or item labels) + reports the shipmentIds Amazon didn't have labels
// for yet, via the X-Labels-Skipped response header. Shared by every "print all shipments" fetch.
async function fetchAllLabelsBlob(url, params) {
    const res = await ApiClient.get(url, { params, responseType: 'blob', timeout: 120000 });
    const contentType = res.headers['content-type'] || 'application/pdf';
    const skippedHeader = res.headers['x-labels-skipped'];
    const skipped = skippedHeader ? skippedHeader.split(',').map(s => s.trim()).filter(Boolean) : [];
    return { blob: new Blob([res.data], { type: contentType }), skipped };
}

// Task C8: pallet labels -- four per pallet, one per side, straight from Amazon's v0 getLabels
// (numberOfPallets). Separate endpoint from box labels because Amazon returns a separate document:
// box labels identify the cartons, pallet labels identify the handling units the carrier and
// receiving dock scan. No `count` param -- the backend derives numberOfPallets from the shipment's
// own measured pallet build, never from a UI hint.
async function fetchPalletLabelBlob(planId, shipmentId, { pageType = DEFAULT_LABEL_PAGE_TYPE } = {}) {
    const res = await ApiClient.get(
        `/api/shipment-planner/pallet-labels/${planId}/${shipmentId}`,
        { params: { pageType }, responseType: 'blob', timeout: 90000 }
    );
    const contentType = res.headers['content-type'] || 'application/pdf';
    return new Blob([res.data], { type: contentType });
}

async function fetchItemLabelBlob(planId, shipmentId) {
    const res = await ApiClient.get(
        `/api/shipment-planner/item-labels/${planId}/${shipmentId}`,
        { responseType: 'blob', timeout: 90000 }
    );
    const contentType = res.headers['content-type'] || 'application/pdf';
    return new Blob([res.data], { type: contentType });
}

async function describeBlobError(err) {
    let msg = err.message || 'Label fetch failed';
    try {
        if (err.response?.data instanceof Blob) {
            const parsed = JSON.parse(await err.response.data.text());
            if (parsed.error) msg = parsed.error;
        } else if (err.response?.data?.error) {
            msg = err.response.data.error;
        }
    } catch (ignore) { /* keep generic message */ }
    return msg;
}

// "Printed N of M shipments -- X, Y not ready yet" -- surfaced whenever the plan-level endpoints
// report skipped shipments, so a not-ready shipment never silently vanishes from the print run.
function skippedToastMessage(skipped, totalCount) {
    if (!skipped || skipped.length === 0) return null;
    const prefix = totalCount != null
        ? `Printed ${totalCount - skipped.length} of ${totalCount} shipments`
        : `${skipped.length} shipment${skipped.length > 1 ? 's' : ''} skipped`;
    return `${prefix} — ${skipped.join(', ')} not ready yet.`;
}

const FALLBACK_TOAST = 'Opened PDF — press Ctrl+P to print (direct print isn\'t supported in this browser).';

function isFirefoxBrowser() {
    return typeof navigator !== 'undefined' && /firefox/i.test(navigator.userAgent || '');
}

// Reads a Blob into a base64 string (no data: URL prefix) for print-js's `base64: true` mode --
// see Task 56 comment above for why we hand print-js bytes instead of a blob: URL.
function blobToBase64(blob) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onerror = () => reject(reader.error || new Error('Failed to read PDF data'));
        reader.onloadend = () => {
            const result = reader.result || '';
            const commaIdx = result.indexOf(',');
            resolve(commaIdx === -1 ? result : result.slice(commaIdx + 1));
        };
        reader.readAsDataURL(blob);
    });
}

// Shared "give the user a usable PDF" fallback: opens the blob in a new tab. Used both for the
// deliberate "open PDF" action (no toast -- that's just the button doing its job) and for every
// print-path failure/unsupported-browser case (with a toast so the user knows why a print dialog
// didn't show up and what to do instead).
function openBlobInNewTab(blob, { toastMessage } = {}) {
    const url = URL.createObjectURL(blob);
    const win = window.open(url, '_blank');
    if (!win) {
        toast.error('Pop-up blocked — allow pop-ups for this site to view labels.');
    } else if (toastMessage) {
        toast(toastMessage, { duration: 8000 });
    }
    // The new tab owns the object URL now; revoke it well after the tab has had time to load
    // the PDF rather than immediately (revoking too early blanks the tab).
    setTimeout(() => URL.revokeObjectURL(url), 60000);
    return !!win;
}

// Sends a PDF blob to print-js in base64 mode (see Task 56 comment above) and resolves once the
// print dialog has closed. Firefox is carved out up front rather than left to fail into onError.
// Any failure -- Firefox, print-js's onError, or a synchronous throw -- resolves through the same
// "open the PDF so it's still usable" fallback; the error path never dead-ends on just a toast.
// A fallback timer guards the (rare) case where neither onError nor onPrintDialogClose ever
// fires, so the caller's busy/loading UI state can't get stuck forever.
function printPdfBlob(blob) {
    if (isFirefoxBrowser()) {
        openBlobInNewTab(blob, { toastMessage: FALLBACK_TOAST });
        return Promise.resolve();
    }

    return blobToBase64(blob)
        .catch((e) => {
            console.error('Failed to read PDF for printing:', e);
            return null;
        })
        .then((base64) => {
            if (base64 == null) {
                openBlobInNewTab(blob, { toastMessage: FALLBACK_TOAST });
                return undefined;
            }

            return new Promise((resolve) => {
                let settled = false;
                let fallbackTimer;
                // Cleanup here is just resolving the promise (so callers' busy state clears) --
                // print-js owns the base64 payload's internal blob URL and its own revocation
                // once onPrintDialogClose's underlying focus/mouseover listener fires, so there's
                // nothing of ours left to clean up (unlike the old url-mode path).
                const finish = () => {
                    if (settled) return;
                    settled = true;
                    clearTimeout(fallbackTimer);
                    resolve();
                };
                const fallbackToTab = () => {
                    openBlobInNewTab(blob, { toastMessage: FALLBACK_TOAST });
                    finish();
                };
                fallbackTimer = setTimeout(finish, 60000);

                try {
                    printJS({
                        printable: base64,
                        type: 'pdf',
                        base64: true,
                        onError: (error) => {
                            console.error('print-js failed:', error);
                            fallbackToTab();
                        },
                        onPrintDialogClose: finish,
                    });
                } catch (e) {
                    console.error('print-js threw synchronously:', e);
                    fallbackToTab();
                }
            });
        });
}

// Fetches the label PDF and sends it straight to the browser's print command -- no intermediate
// tab/download. This is the default action behind every "Print labels" button.
export async function printShipmentLabels(planId, shipmentId, opts = {}) {
    try {
        const blob = await fetchLabelBlob(planId, shipmentId, opts);
        await printPdfBlob(blob);
        return true;
    } catch (err) {
        console.error('Label fetch failed:', err);
        toast.error(await describeBlobError(err), { duration: 8000 });
        return false;
    }
}

// Secondary affordance: the pre-Task-52 behavior of opening the PDF in a new tab, kept around so
// labels can still be saved or reprinted elsewhere without going through the direct-print path.
export async function openShipmentLabelsPdf(planId, shipmentId, opts = {}) {
    try {
        const blob = await fetchLabelBlob(planId, shipmentId, opts);
        openBlobInNewTab(blob);
        return true;
    } catch (err) {
        console.error('Label fetch failed:', err);
        toast.error(await describeBlobError(err), { duration: 8000 });
        return false;
    }
}

// Task C8: pallet labels -- printed only after the shipment's freight was priced from MEASURED
// pallets and confirmed (the backend's phase gate 400s otherwise with an explanatory message).
// Mirrors printShipmentLabels/openShipmentLabelsPdf exactly, against the pallet-labels endpoint.
export async function printPalletLabels(planId, shipmentId, opts = {}) {
    try {
        const blob = await fetchPalletLabelBlob(planId, shipmentId, opts);
        await printPdfBlob(blob);
        return true;
    } catch (err) {
        console.error('Pallet label fetch failed:', err);
        toast.error(await describeBlobError(err), { duration: 8000 });
        return false;
    }
}

export async function openPalletLabelsPdf(planId, shipmentId, opts = {}) {
    try {
        const blob = await fetchPalletLabelBlob(planId, shipmentId, opts);
        openBlobInNewTab(blob);
        return true;
    } catch (err) {
        console.error('Pallet label fetch failed:', err);
        toast.error(await describeBlobError(err), { duration: 8000 });
        return false;
    }
}

// Task 57: box/carrier labels for EVERY shipment in the plan, merged server-side into one PDF --
// one click, one print dialog, every box across every shipment. `totalCount` (the number of
// shipments the caller already knows about, e.g. from GET /shipment-planner/confirmed) drives the
// "printed N of M" phrasing in the skipped-shipment toast; omit it for a simpler message.
export async function printAllShipmentLabels(planId, { pageType = DEFAULT_LABEL_PAGE_TYPE, totalCount } = {}) {
    try {
        const { blob, skipped } = await fetchAllLabelsBlob(
            `/api/shipment-planner/labels/${planId}/all`, { pageType });
        await printPdfBlob(blob);
        const msg = skippedToastMessage(skipped, totalCount);
        if (msg) toast(msg, { duration: 10000 });
        return true;
    } catch (err) {
        console.error('Print-all label fetch failed:', err);
        toast.error(await describeBlobError(err), { duration: 8000 });
        return false;
    }
}

export async function openAllShipmentLabelsPdf(planId, { pageType = DEFAULT_LABEL_PAGE_TYPE, totalCount } = {}) {
    try {
        const { blob, skipped } = await fetchAllLabelsBlob(
            `/api/shipment-planner/labels/${planId}/all`, { pageType });
        openBlobInNewTab(blob);
        const msg = skippedToastMessage(skipped, totalCount);
        if (msg) toast(msg, { duration: 10000 });
        return true;
    } catch (err) {
        console.error('Print-all label fetch failed:', err);
        toast.error(await describeBlobError(err), { duration: 8000 });
        return false;
    }
}

// Task 57 owner addition #2 (verbatim): "create another button to print on the Dymo 550 printer -
// this will print the merchant labels needed for the shipment" -- the per-UNIT FNSKU labels
// (2024-03-20 createMarketplaceItemLabels), distinct from the per-BOX labels above. No pageType/
// count -- Amazon sizes these for the DYMO 550 server-side and the document isn't cached.
export async function printShipmentItemLabels(planId, shipmentId) {
    try {
        const blob = await fetchItemLabelBlob(planId, shipmentId);
        await printPdfBlob(blob);
        return true;
    } catch (err) {
        console.error('Item label fetch failed:', err);
        toast.error(await describeBlobError(err), { duration: 8000 });
        return false;
    }
}

export async function openShipmentItemLabelsPdf(planId, shipmentId) {
    try {
        const blob = await fetchItemLabelBlob(planId, shipmentId);
        openBlobInNewTab(blob);
        return true;
    } catch (err) {
        console.error('Item label fetch failed:', err);
        toast.error(await describeBlobError(err), { duration: 8000 });
        return false;
    }
}

export async function printAllShipmentItemLabels(planId, { totalCount } = {}) {
    try {
        const { blob, skipped } = await fetchAllLabelsBlob(
            `/api/shipment-planner/item-labels/${planId}/all`, {});
        await printPdfBlob(blob);
        const msg = skippedToastMessage(skipped, totalCount);
        if (msg) toast(msg, { duration: 10000 });
        return true;
    } catch (err) {
        console.error('Print-all item label fetch failed:', err);
        toast.error(await describeBlobError(err), { duration: 8000 });
        return false;
    }
}

export async function openAllShipmentItemLabelsPdf(planId, { totalCount } = {}) {
    try {
        const { blob, skipped } = await fetchAllLabelsBlob(
            `/api/shipment-planner/item-labels/${planId}/all`, {});
        openBlobInNewTab(blob);
        const msg = skippedToastMessage(skipped, totalCount);
        if (msg) toast(msg, { duration: 10000 });
        return true;
    } catch (err) {
        console.error('Print-all item label fetch failed:', err);
        toast.error(await describeBlobError(err), { duration: 8000 });
        return false;
    }
}

// Task 49 (owner, re: Runs page screenshot of run #115 CONFIRMED with only Open/Cancel-shipment
// actions): "the print action should be available where confirmed runs appear, not only on the
// dedicated [Confirmed Shipments] page." Drop this in anywhere a CONFIRMED run row/banner is
// rendered -- on mount it looks up the run's shipment(s) from GET /api/shipment-planner/confirmed
// (matched by runId, since that endpoint is the only source of shipmentId/boxCount/destination
// for a run) so the primary buttons can show "M shipments, N boxes".
//
// Task 57 redesign (owner, verbatim, re: multiple labels/boxes per shipment): the primary action
// is now "print every box across every shipment in one dialog" (printAllShipmentLabels), not a
// per-shipment chooser -- multi-shipment runs no longer require picking one shipment first.
// Individual per-shipment reprints (and the DYMO item-label buttons) live behind the gear (⚙)
// popover for the less-common case of reprinting just one shipment.
export default function PrintLabelsAction({ runId, planId, className, label = 'Print all labels' }) {
    const [shipments, setShipments] = useState(null);
    const [busyKey, setBusyKey] = useState(null); // 'ALL' | 'ALL_ITEM' | shipmentId | `${shipmentId}:item`
    const [open, setOpen] = useState(false);
    const [pageType, setPageType] = useState(DEFAULT_LABEL_PAGE_TYPE);
    const boxRef = useRef(null);

    useEffect(() => {
        if (!planId || !runId) return undefined;
        let cancelled = false;
        ApiClient.get('/api/shipment-planner/confirmed')
            .then((res) => {
                if (cancelled) return;
                const rows = res.data || [];
                const entry = rows.find(r => String(r.runId) === String(runId));
                setShipments(entry?.shipments || []);
            })
            .catch((err) => {
                if (!cancelled) console.error('Failed to preload shipment details for print-all:', err);
            });
        return () => { cancelled = true; };
    }, [runId, planId]);

    useEffect(() => {
        if (!open) return undefined;
        const onClick = (e) => {
            if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener('mousedown', onClick);
        return () => document.removeEventListener('mousedown', onClick);
    }, [open]);

    const shipmentCount = shipments?.length ?? null;
    const boxTotal = shipments && shipments.every(s => s.boxCount != null)
        ? shipments.reduce((sum, s) => sum + s.boxCount, 0)
        : null;
    const buttonLabel = shipmentCount != null
        ? `${label} — ${shipmentCount} shipment${shipmentCount === 1 ? '' : 's'}`
            + (boxTotal != null ? `, ${boxTotal} box${boxTotal === 1 ? '' : 'es'}` : '')
        : label;

    const requirePlan = () => {
        if (planId) return true;
        toast.error('No plan id on this run — labels unavailable.');
        return false;
    };

    const handlePrintAll = async () => {
        if (!requirePlan()) return;
        setBusyKey('ALL');
        await printAllShipmentLabels(planId, { pageType, totalCount: shipmentCount ?? undefined });
        setBusyKey(null);
    };

    const handleOpenAll = async () => {
        if (!requirePlan()) return;
        setBusyKey('ALL');
        await openAllShipmentLabelsPdf(planId, { pageType, totalCount: shipmentCount ?? undefined });
        setBusyKey(null);
    };

    const handlePrintAllItems = async () => {
        if (!requirePlan()) return;
        setBusyKey('ALL_ITEM');
        await printAllShipmentItemLabels(planId, { totalCount: shipmentCount ?? undefined });
        setBusyKey(null);
    };

    const handleOpenAllItems = async () => {
        if (!requirePlan()) return;
        setBusyKey('ALL_ITEM');
        await openAllShipmentItemLabelsPdf(planId, { totalCount: shipmentCount ?? undefined });
        setBusyKey(null);
    };

    const reprintOne = async (ship) => {
        setBusyKey(ship.shipmentId);
        await printShipmentLabels(planId, ship.shipmentId, { pageType });
        setBusyKey(null);
    };

    const reprintOneItem = async (ship) => {
        setBusyKey(`${ship.shipmentId}:item`);
        await printShipmentItemLabels(planId, ship.shipmentId);
        setBusyKey(null);
    };

    const busy = busyKey != null;

    return (
        <div className="relative inline-flex items-center gap-1.5" ref={boxRef}>
            <button
                onClick={handlePrintAll}
                disabled={busy}
                className={className || 'flex items-center gap-1 text-xs text-indigo-600 hover:text-indigo-800 hover:underline disabled:opacity-50'}
                title="Fetch and print every box's UPS + FBA label across every shipment in one dialog, 4×6 thermal by default"
            >
                <PrinterIcon className="h-3.5 w-3.5" />
                {busyKey === 'ALL' ? 'Printing…' : buttonLabel}
            </button>
            <button
                onClick={handlePrintAllItems}
                disabled={busy}
                className="flex items-center gap-1 text-xs text-gray-500 hover:text-indigo-600 hover:underline disabled:opacity-50"
                title="FNSKU unit labels — select the DYMO 550 in the print dialog; silent printing only works to the station's default printer"
            >
                <TagIcon className="h-3.5 w-3.5" />
                {busyKey === 'ALL_ITEM' ? 'Printing…' : 'Print item labels (DYMO)'}
            </button>
            <button
                onClick={() => setOpen(o => !o)}
                disabled={busy}
                className="p-1 text-gray-400 hover:text-indigo-600 disabled:opacity-50"
                title="More options: page type, open PDF, reprint a single shipment"
            >
                <Cog6ToothIcon className="h-3.5 w-3.5" />
            </button>
            {open && (
                <div className="absolute right-0 top-full z-20 mt-1 w-80 rounded-md border border-gray-200 bg-white shadow-lg p-2">
                    <div className="flex items-center justify-between gap-2 px-1 pb-2">
                        <label className="text-[11px] text-gray-500">Box label page type</label>
                        <select
                            value={pageType}
                            onChange={(e) => setPageType(e.target.value)}
                            className="rounded border border-gray-300 px-1.5 py-1 text-xs text-gray-700"
                        >
                            {PAGE_TYPES.map(pt => <option key={pt.value} value={pt.value}>{pt.label}</option>)}
                        </select>
                    </div>
                    <div className="flex flex-col gap-1">
                        <button
                            onClick={handleOpenAll}
                            disabled={busy}
                            className="w-full text-left px-2 py-1.5 text-xs text-gray-600 hover:bg-indigo-50 rounded disabled:opacity-50"
                        >
                            open all box labels PDF
                        </button>
                        <button
                            onClick={handleOpenAllItems}
                            disabled={busy}
                            className="w-full text-left px-2 py-1.5 text-xs text-gray-600 hover:bg-indigo-50 rounded disabled:opacity-50"
                        >
                            open all item labels PDF
                        </button>
                    </div>
                    {shipments && shipments.length > 1 && (
                        <>
                            <div className="text-[11px] text-gray-400 px-1 pt-2 pb-1 border-t border-gray-100 mt-1">
                                Reprint a single shipment
                            </div>
                            <div className="flex flex-col gap-1 max-h-40 overflow-y-auto">
                                {shipments.map(ship => (
                                    <div
                                        key={ship.shipmentId}
                                        className="flex items-center justify-between gap-2 px-2 py-1 text-xs rounded hover:bg-indigo-50"
                                    >
                                        <span className="font-mono text-gray-700 truncate">
                                            {ship.destinationFc || '—'}
                                            <span className="text-gray-400 font-sans"> · {ship.boxCount ?? ship.units ?? '—'} boxes</span>
                                        </span>
                                        <span className="flex items-center gap-2 whitespace-nowrap">
                                            <button
                                                onClick={() => reprintOne(ship)}
                                                disabled={busyKey === ship.shipmentId}
                                                className="text-indigo-600 hover:underline disabled:opacity-50"
                                                title="Print this shipment's box labels"
                                            >
                                                {busyKey === ship.shipmentId ? '…' : 'labels'}
                                            </button>
                                            <button
                                                onClick={() => reprintOneItem(ship)}
                                                disabled={busyKey === `${ship.shipmentId}:item`}
                                                className="text-gray-500 hover:text-indigo-600 hover:underline disabled:opacity-50"
                                                title="Print this shipment's FNSKU item labels (DYMO 550)"
                                            >
                                                {busyKey === `${ship.shipmentId}:item` ? '…' : 'item'}
                                            </button>
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </>
                    )}
                </div>
            )}
        </div>
    );
}
